const HEADING_SELECTORS = ["main h1", "h1"];
const ATTEMPTS = 20;
const INTERVAL_MS = 100;

interface FocusMainHeadingOptions {
	/**
	 * Skip the heading on the page when this is called: the caller has just
	 * navigated, and that heading belongs to the page being left. It is still
	 * focused on the last attempt if no other heading has appeared by then.
	 */
	waitForNewPage?: boolean;
}

/** The pending attempt of the latest call, so a newer call can replace it. */
let pendingAttempt: number | undefined;

/** Stops a focus attempt that is still waiting for its heading. */
export function cancelMainHeadingFocus(): void {
	if (pendingAttempt !== undefined) {
		window.clearTimeout(pendingAttempt);
		pendingAttempt = undefined;
	}
}

function findHeading(): HTMLElement | null {
	for (const selector of HEADING_SELECTORS) {
		const element = document.querySelector<HTMLElement>(selector);
		if (element) return element;
	}
	return null;
}

/**
 * Move focus to the page's main heading once it renders. The page that
 * appears after a role switch may load before showing its heading, so this
 * keeps looking for up to two seconds and then gives up, leaving focus where
 * it is.
 */
export function focusMainHeading(options: FocusMainHeadingOptions = {}): void {
	// A newer switch replaces an older request that is still looking, so the
	// two cannot fight over focus.
	cancelMainHeadingFocus();
	const previous = options.waitForNewPage ? findHeading() : null;
	let attempts = 0;
	const tryFocus = (): void => {
		attempts += 1;
		const heading = findHeading();
		const lastAttempt = attempts >= ATTEMPTS;
		if (heading && (heading !== previous || lastAttempt)) {
			if (!heading.hasAttribute("tabindex")) {
				heading.setAttribute("tabindex", "-1");
			}
			heading.focus();
			pendingAttempt = undefined;
			return;
		}
		pendingAttempt = lastAttempt
			? undefined
			: window.setTimeout(tryFocus, INTERVAL_MS);
	};
	pendingAttempt = window.setTimeout(tryFocus, 0);
}
